import { useCallback, useState } from 'react';
import { ApiError } from '@bymason/utils';
import { getErrorCode, getErrorData, getErrorMessage } from './utils';

export type SaveHook<CriteriaType, ResultType, ErrorDataType> = [
  execute: (dataToSave: CriteriaType) => void,
  result: ResultType,
  error: string | null,
  busy: boolean,
  errorCode: number,
  errorData: ErrorDataType | undefined,
  clearData: () => void
];

export function useSaveDataHook<O, T, E = void>(onSaveCallback: (options: O) => Promise<T>) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [errorCode, setErrorCode] = useState(0);
  const [errorData, setErrorData] = useState<E>();

  const saveData = useCallback(
    async (options: O) => {
      try {
        setBusy(true);
        setError(null);
        setErrorCode(0);

        const newData = await onSaveCallback(options);

        setData(newData);
      } catch (error) {
        const err = error as Error | ApiError<E>;
        setError(getErrorMessage(err));
        setErrorCode(getErrorCode(err));
        setErrorData(getErrorData<E>(err));
      } finally {
        setBusy(false);
      }
    },
    [onSaveCallback]
  );

  const clearData = useCallback(() => {
    setData(null);
  }, []);

  return [saveData, data, error, busy, errorCode, errorData, clearData] as SaveHook<O, T, E>;
}
